/**
 * Production locales (noping.com) and the closest one for each country/language pair language. Dependency-free on
 * purpose: routes.ts and links.ts both import it (links.ts is the public resolver and re-exports these names).
 */

export const PRODUCTION_ORIGIN = 'https://noping.com';

/** Locales that exist on production (next-i18next.config.js). */
export const PRODUCTION_LOCALES = ['en', 'pt', 'es', 'fr', 'ru', 'tr', 'sa', 'cn', 'jp', 'kr'] as const;
export type ProductionLocale = (typeof PRODUCTION_LOCALES)[number];
export const PRODUCTION_FALLBACK_LOCALE: ProductionLocale = 'en';
export const PRODUCTION_LOCALE_SET: ReadonlySet<string> = new Set<string>(PRODUCTION_LOCALES);

/**
 * Pair language (manifest language_code or its content group, case-insensitive) → closest production locale. A code
 * absent here is looked up by its base subtag (pt-AO → pt, zh-Hant → zh, yue-Hant → yue) and then falls back to English.
 */
export const PRODUCTION_LOCALE_BY_LANGUAGE: Readonly<Record<string, ProductionLocale>> = {
  en: 'en',
  pt: 'pt',
  es: 'es',
  // Catalan and the Indigenous languages of Spanish-speaking markets: Spanish is the shared language there.
  ca: 'es',
  qu: 'es',
  quy: 'es',
  quc: 'es',
  nah: 'es',
  fr: 'fr',
  // Haitian Creole is French-based.
  ht: 'fr',
  ru: 'ru',
  // Belarusian, Kazakh and Tatar: Russian is co-official / the shared language in those markets.
  be: 'ru',
  kk: 'ru',
  tt: 'ru',
  tr: 'tr',
  // South Azerbaijani is Turkic, largely intelligible with Turkish.
  azb: 'tr',
  ar: 'sa',
  // Mehri (Yemen): Arabic is the written language of that market.
  gdq: 'sa',
  zh: 'cn',
  yue: 'cn',
  ja: 'jp',
  ko: 'kr',
};

export function productionLocale(language: string | null | undefined): ProductionLocale {
  const code = (language ?? '').trim().toLowerCase();
  if (!code) return PRODUCTION_FALLBACK_LOCALE;
  return PRODUCTION_LOCALE_BY_LANGUAGE[code] ?? PRODUCTION_LOCALE_BY_LANGUAGE[code.split('-')[0] ?? ''] ?? PRODUCTION_FALLBACK_LOCALE;
}

/** https://noping.com/<locale><path> ('/' or '' = the locale home). Every production link carries its locale, en included. */
export function productionHref(language: string | null | undefined, path = '/'): string {
  const clean = path === '/' ? '' : path;
  return `${PRODUCTION_ORIGIN}/${productionLocale(language)}${clean}`;
}
